import { useCallback } from 'react';
import {
  Outlet,
  Link,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router';
import { Tabs, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import { Button } from 'src/components/ui/button';
import { PlusIcon } from '@phosphor-icons/react';
import paths from 'src/paths';
import {
  ForegroundAgentChat,
  type EmailRuleConfig,
} from '@/components/common/ForegroundAgentChat';
import { rpcClient } from '@/api/rpc/client';

export function WorkflowsPage() {
  const firstChild = useChildMatches({ select: (matches) => matches[0] });
  const navigate = useNavigate();

  const handleCreateWorkflowWithEmailRule = useCallback(
    async (config: EmailRuleConfig) => {
      const filters: {
        from?: string[];
        subject?: string[];
      } = {};

      if (config.filters.subjectContains) {
        filters.subject = [config.filters.subjectContains];
      }
      if (config.filters.senderDomainContains) {
        filters.from = [config.filters.senderDomainContains];
      }
      if (config.filters.senderEmailContains) {
        filters.from = [
          ...(filters.from ?? []),
          config.filters.senderEmailContains,
        ];
      }

      const response = await rpcClient.communications.emailRules.create({
        ruleName: config.ruleName,
        filters,
      });

      await rpcClient.communications.emailRules.events.createAndLink({
        ruleId: response.rule.id,
        name: config.eventName,
      });
    },
    [],
  );

  // Determine active tab from route pathname
  const activeRoute = firstChild?.routeId;
  let activeTab = 'definitions';
  if (activeRoute === '/authenticated/workflows/templates') {
    activeTab = 'templates';
  } else if (activeRoute === '/authenticated/workflows/') {
    activeTab = 'definitions';
  } else {
    activeTab = 'definition-details';
  }

  // this let's us maintain the breadcrumbs without having to render the tabs
  if (activeTab === 'definition-details') {
    return <Outlet />;
  }

  const handleCreateWorkflow = () => {
    void navigate({
      to: paths.authenticated.workflows
        .create as '/workflows/$workflowDefinitionId',
      params: { workflowDefinitionId: 'new' },
      search: { edit: true },
    });
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Workflows</h1>
          <p className="text-muted-foreground mt-1">
            Manage and configure your workflows
          </p>
        </div>
        <Button onClick={handleCreateWorkflow}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Workflow
        </Button>
      </div>

      <Tabs value={activeTab} className="w-full">
        <TabsList>
          <TabsTrigger value="definitions" asChild>
            <Link to={paths.authenticated.workflows.list}>Definitions</Link>
          </TabsTrigger>
          <TabsTrigger value="templates" asChild>
            <Link to={paths.authenticated.workflows.templates}>Templates</Link>
          </TabsTrigger>
        </TabsList>
        <div className="mt-4">
          <Outlet />
        </div>
      </Tabs>
      <ForegroundAgentChat
        moduleKey="ap"
        currentPage="/workflows"
        onNavigate={(path, options) =>
          navigate({ to: path as any, search: options?.search as any })
        }
        onCreateWorkflowWithEmailRule={handleCreateWorkflowWithEmailRule}
      />
    </div>
  );
}
